


















































































































































































































import Vue from 'vue';
import Component from 'vue-class-component';
import { Watch } from 'vue-property-decorator';
import draggable from 'vuedraggable';
import { Props, User, Schedules, Global, Groups } from '@/store';
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue';
import PropEditor from '@/components/PropEditor.vue';
import MessageDialog from '@/components/dialogs/MessageDialog.vue';
import cloneDeep from 'lodash/cloneDeep';
import { Types } from '@/modules/API';
import { getScheduleExpiration } from '@/modules/ScheduleUtils';
import ScheduleExpirationBadge from '@/components/ScheduleExpirationBadge.vue';
import TableCellChips from '@/components/TableCellChips.vue';

@Component({
  components: {ConfirmDialog, PropEditor, draggable, MessageDialog, ScheduleExpirationBadge, TableCellChips}
})
class ScheduleManagement extends Vue {
  @User.State('project') project;
  @Props.State('list') properties;
  @Schedules.Action('loadSchedules') loadSchedules;
  @Schedules.State('schedules') schedules;
  @Schedules.Mutation('schedules') setSchedules;
  @Schedules.Action('runSchedulerInServer') runSchedulerInServer;
  @Groups.State('list') groups;
  @Groups.State('isLoading') groups_loading;
  @Groups.Action('load') loadGroups;
  @Global.State('lang') lang;
  @Global.Mutation('setPageTitle') setPageTitle;
  @Global.State('timezone') projectTimezone;
  @Global.State('readonly_user') readonly_user;

  isMessageDialogOpen = false;
  message = 'There is no timezone for this project';
  info = 'Please contact with operation team';
  currentHeaderClass = '';
  isLoading = false;
  group = null;
  deleteDialog = false;
  schedule_to_delete = null;
  has_changes = false;
  saveDialog = false;
  schedules_copy = [];
  route_to = null;
  saveMessage = '';
  checkbox_color = '#812E81';
  search = '';
  group_filter = [];

  mounted() {
    this.setPageTitle('Schedule Management');
    this.load();
  }

  updated() {
    this.currentHeaderClass = this.lang === 'en' ? 'text-xs-left' : 'text-xs-right';
  }

  /** Group id -> group name, for resolving the ids stored on each schedule. */
  get groups_names_map(){
    const map = {};
    (this.groups || []).forEach((group) => map[group.id] = group.name);
    return map;
  }

  /*
    Schedule id -> the groups it is applied to, name sorted, so a chip can link to one.
    Groups arrive page by page while the schedules are already on screen, so an id whose
    name is not known yet is left out rather than rendered raw - the user used to watch
    group ids sit in the column and turn into names a moment later. Once the groups
    finished loading, an unresolved id means the group is gone and the id is all we have.
  */
  get schedule_groups_map(){
    const map = {};
    this.schedules_copy.forEach((schedule) => {
      map[schedule.id] = (Array.isArray(schedule.groups) ? schedule.groups : [])
        .filter((group_id) => this.groups_names_map[group_id] || !this.groups_loading)
        .map((group_id) => ({ id: group_id, name: this.groups_names_map[group_id] || group_id }))
        .sort((group1, group2) => `${group1.name}`.localeCompare(`${group2.name}`));
    });
    return map;
  }

  /*
    Schedule id -> shared expiration calculation. Derived from the current date on every
    recompute, so nothing about the remaining time is persisted on the schedule itself.
  */
  get schedule_expiration_map(){
    const map = {};
    this.schedules_copy.forEach((schedule) => {
      map[schedule.id] = getScheduleExpiration(schedule, this.projectTimezone);
    });
    return map;
  }

  /*
    Only groups actually applied to a schedule are worth filtering by. Built off the map
    above so the filter never offers a raw group id either.
  */
  get filter_groups_list(){
    const groups_by_id = new Map();
    Object.keys(this.schedule_groups_map).forEach((schedule_id) =>
      this.schedule_groups_map[schedule_id].forEach((group) => groups_by_id.set(group.id, group)));

    return [...groups_by_id.values()]
      .sort((group1: any, group2: any) => `${group1.name}`.localeCompare(`${group2.name}`));
  }

  /*
    Whether the project has any schedules at all, which is what the empty state answers -
    `filtered_schedules` being empty can also just mean the filters excluded everything.
  */
  get has_schedules(){
    return this.schedules_copy.length > 0;
  }

  get is_filtering(){
    return !!(this.search || '').trim() || this.group_filter.length > 0;
  }

  get filtered_schedules(){
    const query = (this.search || '').trim().toLowerCase();

    return this.schedules_copy.filter((schedule) => {
      const matches_search = !query || `${schedule.name || ''}`.toLowerCase().includes(query);
      const matches_groups = !this.group_filter.length ||
        (Array.isArray(schedule.groups) && schedule.groups.some((group_id) => this.group_filter.includes(group_id)));

      return matches_search && matches_groups;
    });
  }

  /** Reordering by drag only makes sense while the whole, unfiltered list is on screen. */
  get is_draggable(){
    return !this.readonly_user && !this.is_filtering;
  }

  clearFilters(){
    this.search = '';
    this.group_filter = [];
  }

  handleSaveClicked(){
    this.saveDialog = true;
    this.saveMessage = 'Are you sure you want to save changes?';
  }

  handleSaveCancled(){
    this.saveDialog = false;
    if (this.route_to) {
      this.has_changes = false;
      this.$router.push(this.route_to.fullPath);
    }
  }

  goToSchedule(){
    this.projectTimezone
      ? this.$router.push('/schedules/create')
      : this.isMessageDialogOpen = true;
  }

  handleCheckbox(item){
    this.has_changes = true;
    const schedule = this.schedules_copy.find((schedule) => schedule.id === item.id);
    schedule.active = item.active ? 0 : 1;
  }

  handleChange(){
    this.has_changes = true;
    this.schedules_copy.forEach((schedule, index) => schedule.priority = index + 1);
  }

  updateScheduleProperty(){
    const schedule_property = this.properties.find((item) => item.name === 'dashboard.schedules');
    if (schedule_property.value) {
      schedule_property.value = this.schedules_copy;
      this.setSchedules(schedule_property);
    }
  }

  @Watch('schedules') 
  handleSchedules(){
    this.schedules_copy = cloneDeep(this.schedules);
  }

  @Watch('project')
  async load() {
    if (this.project){
      this.isLoading = true;
      this.clearFilters();
      await Promise.all([
        this.loadSchedules({projectId: this.project.id}),
        this.loadGroups({project_id: this.project.id})
      ]);
      this.isLoading = false;
    }
  }

  removeSchedule() {
    this.has_changes = true;
    this.schedules_copy = this.schedules_copy.filter((schedule) => schedule.id !== this.schedule_to_delete);
    this.schedules_copy.forEach((schedule, index) => schedule.priority = index + 1);
    this.schedule_to_delete = null;
    this.deleteDialog = false;
  }

  openDeleteDialog(schedule_id) {
    this.deleteDialog = true;
    this.schedule_to_delete = schedule_id;
  }

  async saveChanges(){
    (this.$refs.propEditor as Vue & { save: (value: any, prop: any, callback: () => void) => void }).save(this.schedules_copy, {
      name: 'dashboard.schedules',
      objectID: this.project.id,
      objectType: Types.PROJECTS
    }, () => {
      this.saveDialog = false;
      this.$notify({
        text: 'Schedules Updated Successfully',
        type: 'success',
        title: 'Success'
      });
      this.runSchedulerInServer();
      this.has_changes = false;
      if (this.route_to) this.$router.push(this.route_to.fullPath);
    });
  }

  beforeRouteLeave(to, from, next) {
    this.route_to = to;

    if (this.has_changes) {
      this.saveMessage = 'Save changes before exiting?';
      this.saveDialog = true;
    }else next();
  }
}

export default ScheduleManagement;
